import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { ClientDeviceStats } from '../../services/statusService';
import { deviceTypeColor, deviceTypeLabel, percentOf } from './deviceTypes';
import { groupClientStats } from './clientGroups';

/** The rows as CSV (semicolon separated, Excel-friendly), in the order shown. */
export const clientsCsv = (rows: ClientDeviceStats[], headers: string[]): string => {
  const cell = (v: string | number) => {
    const s = String(v);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((c) => [
    c.client_name, c.total_devices, c.total_devices - c.devices_needing_refresh, c.devices_needing_refresh,
    c.devices_by_type.map((x) => `${x.device_type} ${x.count}`).join(' | '),
  ].map(cell).join(';'));
  return [headers.map(cell).join(';'), ...lines].join('\n');
};

const downloadText = (name: string, text: string) => {
  const blob = new Blob([`\ufeff${text}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

/*
 * Every client with its fleet: devices, up to date, waiting for a
 * refresh (count, share and a small bar), and the gateway types. A
 * filter box, sortable columns, a bounded height that scrolls, and a
 * link to the clients page for the rest.
 */

type SortKey = 'client_name' | 'total_devices' | 'up_to_date' | 'devices_needing_refresh';

interface Props {
  clients: ClientDeviceStats[];
  loading?: boolean;
  /** The filter text, when the dashboard drives it (a click on a bar). */
  filter: string;
  onFilterChange: (value: string) => void;
}

const upToDate = (c: ClientDeviceStats) => c.total_devices - c.devices_needing_refresh;

/** One line of the table: a client (its group's figures when it has agencies) or one of its agencies. */
interface Line { stats: ClientDeviceStats; depth: 0 | 1; agencyCount: number; expanded: boolean }

export const ClientsStatsTable: React.FC<Props> = ({ clients, loading = false, filter, onFilterChange }) => {
  const { t, formatNumber } = useI18n();
  const setFilter = onFilterChange;
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'total_devices', dir: 'desc' });
  const [opened, setOpened] = useState<Set<string>>(new Set());

  // Groups, not clients: a root with agencies is one line with the group's
  // figures, its agencies beneath when opened — by the chevron, by a filter
  // naming the root (a click on its bar) or one of its agencies.
  const { lines, groupsShown, groupsTotal } = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matches = (c: ClientDeviceStats) => c.client_name.toLowerCase().includes(needle);
    const groups = groupClientStats(clients);
    const kept = needle ? groups.filter((g) => matches(g.root) || g.agencies.some(matches)) : groups;
    const value = (c: ClientDeviceStats) => (sort.key === 'up_to_date' ? upToDate(c) : sort.key === 'client_name' ? c.client_name.toLowerCase() : c[sort.key]);
    kept.sort((a, b) => {
      const va = value(a.total); const vb = value(b.total);
      const cmp = typeof va === 'string' && typeof vb === 'string' ? va.localeCompare(vb) : Number(va) - Number(vb);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    const out: Line[] = [];
    kept.forEach((g) => {
      const expanded = g.agencies.length > 0 && (opened.has(g.root.client_id) || (Boolean(needle) && (matches(g.root) || g.agencies.some(matches))));
      out.push({ stats: g.total, depth: 0, agencyCount: g.agencies.length, expanded });
      if (expanded) g.agencies.filter((a) => !needle || matches(a) || matches(g.root)).forEach((a) => out.push({ stats: a, depth: 1, agencyCount: 0, expanded: false }));
    });
    return { lines: out, groupsShown: kept.length, groupsTotal: groups.length };
  }, [clients, filter, sort, opened]);
  const rows = useMemo(() => lines.map((l) => l.stats), [lines]);
  const toggleGroup = (id: string) => setOpened((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const toggleSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : key === 'client_name' ? 'asc' : 'desc' }));

  const header = (key: SortKey, label: string, numeric = false) => (
    <th className={numeric ? 'numeric' : undefined} aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="dash-sort" onClick={() => toggleSort(key)}>
        {label}
        <span className="dash-sort-mark" aria-hidden="true">{sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );

  return (
    <div className="dash-table" data-testid="clients-table">
      <div className="dash-table-toolbar">
        <input
          type="search"
          className="dash-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('dashboard.tableSearch')}
          aria-label={t('dashboard.tableSearch')}
          data-testid="clients-filter"
        />
        <span className="dash-table-count">{t('dashboard.tableShown', { shown: groupsShown, total: groupsTotal })}</span>
        {filter && (
          <button type="button" className="btn btn-link btn-sm dash-clear" onClick={() => setFilter('')} data-testid="clients-filter-clear">
            {t('dashboard.tableClear')}
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm dash-export" disabled={rows.length === 0}
                onClick={() => downloadText('clients.csv', clientsCsv(rows, [t('clients.clientName'), t('dashboard.colDevices'), t('dashboard.seriesUpToDate'), t('dashboard.seriesRefresh'), t('dashboard.deviceTypesColumn')]))}
                data-testid="clients-export">
          {t('dashboard.tableExport')}
        </button>
        <Link to="/clients" className="dash-link">{t('dashboard.tableAll')} →</Link>
      </div>

      {loading && clients.length === 0 ? (
        <div className="dash-empty">{t('dashboard.tableLoading')}</div>
      ) : rows.length === 0 ? (
        <div className="dash-empty">{filter ? t('dashboard.tableNoMatch', { filter: filter.trim() }) : t('dashboard.noClientsToDisplay')}</div>
      ) : (
        <div className="dash-table-wrap">
          <table>
            <thead>
              <tr>
                {header('client_name', t('clients.clientName'))}
                {header('total_devices', t('dashboard.colDevices'), true)}
                {header('up_to_date', t('dashboard.seriesUpToDate'), true)}
                {header('devices_needing_refresh', t('dashboard.seriesRefresh'), true)}
                <th>{t('dashboard.deviceTypesColumn')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(({ stats: c, depth, agencyCount, expanded }) => {
                const share = percentOf(c.devices_needing_refresh, c.total_devices);
                return (
                  <tr key={c.client_id} data-testid="clients-table-row" className={depth ? 'dash-row-agency' : agencyCount > 0 ? 'dash-row-group' : undefined}>
                    <td className="dash-cell-name">
                      {depth === 1 && <span className="dash-tree-branch" aria-hidden="true">└</span>}
                      <Link to={`/devices?client=${encodeURIComponent(c.client_id)}`} className="dash-name-link" title={t('dashboard.seeDevices')}>{c.client_name}</Link>
                      {agencyCount > 0 && (
                        <button type="button" className="dash-tree-toggle" aria-expanded={expanded} data-testid={`toggle-group-${c.client_id}`} onClick={() => toggleGroup(c.client_id)}>
                          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> {t('clients.agencies.count', { count: agencyCount })}
                        </button>
                      )}
                    </td>
                    <td className="numeric">{formatNumber(c.total_devices)}</td>
                    <td className="numeric">{formatNumber(upToDate(c))}</td>
                    <td className="numeric dash-cell-refresh">
                      <span className="dash-refresh-value">{formatNumber(c.devices_needing_refresh)}</span>
                      <span className="dash-refresh-pct">{share} %</span>
                      <span className="dash-mini-track"><span className="dash-mini-fill" style={{ width: `${c.total_devices ? (c.devices_needing_refresh / c.total_devices) * 100 : 0}%` }} /></span>
                    </td>
                    <td>
                      <span className="dash-chips">
                        {c.devices_by_type.length === 0 ? <span className="dash-muted">–</span> : c.devices_by_type.map((x) => (
                          <span key={x.device_type} className="dash-chip" style={{ '--chip': deviceTypeColor(x.device_type) } as React.CSSProperties}>
                            <i className="dash-chip-dot" />{deviceTypeLabel(x.device_type, t('dashboard.unknownType'))} <strong>{formatNumber(x.count)}</strong>
                          </span>
                        ))}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
